import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import validator from '@rjsf/validator-ajv8';

import { API } from 'Amplify';

import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';

import { get, isEmpty, isUndefined, omit, omitBy } from 'lodash';
import {
  generateBatteryDataFromBatteryType,
  generateConverterData,
  generateNewFormConfig,
  generateTariffStructure,
  getBatteryDataOrDefault,
  getBatteryDetailsByBrandModel,
  getConverterDetailsByBrandModel,
  isInvertorHybrid,
  updateConvertersData,
} from '../../shared/device-details-utils';
import { LinearProgress, StepLabel } from '@mui/material';
import { nanoid } from 'nanoid';
import { STEP_IDS, steps } from '../config-wizard-steps';
import { generateStepSchema, getUiSchemaBasedOnSchemaAttributes, isReadyToSave, validateStep } from '../shared';
import SaveDialog from './save-dialog.component';
import { CustomFieldTemplate } from '../../custom-field-template-component/custom-field-template.component';
import type { RJSFValidationError } from '@rjsf/utils';
import { deepClone } from '@mui/x-data-grid/utils/utils';
import { useDeviceDataApiHook } from '../../shared/device-data-api.hook';

const stepperStyles = (theme: any) => ({
  overflowX: 'auto',
  [theme.breakpoints.down('md')]: {
    minHeight: '120px',
  },
  '.MuiStep-horizontal': {
    cursor: 'pointer',
  },
});

const nameUtils = {
  get: (data: any) => data.name || '',
  decode: (name: any) => name?.split('_').join(' '),
  encode: (name: any) => name?.split(' ').join('_'),
  name: function (data: any) {
    return this.decode(this.get(data));
  },
};

const generateDeviceData = (attributes: any, configuration: any, settings: any, schemas: any) => {
  return {
    BASIC: {
      name: nameUtils.name(attributes),
    },
    EMAIL: {
      email: attributes.email,
    },
    ADDRESS: configuration?.addressObj || {},
    CONVERTER: generateConverterData(configuration),
    BATTERY: {
      batteryBrandModel: generateBatteryDataFromBatteryType(get(settings, 'converters[0].battery.batteryType', ''), schemas),
      battery: getBatteryDataOrDefault(get(settings, 'converters[0].battery'), schemas),
    },
    PV: {
      pvArrays: get(settings, 'converters[0].pvArrays', []),
      pvArraysRetrofit: settings.pvArraysRetrofit || [],
      retrofitPVMeasurement: settings.retrofitPVMeasurement || '',
      useUnbiasedEstimators: settings.useUnbiasedEstimators || false,
    },
    ENERGY_TARIFF: generateTariffStructure(schemas, settings),
    METER: {
      meterCurrentLimitation: settings.meterCurrentLimitation || 25,
    },
    PHASES: {
      numberOfPhases: settings.numberOfPhases || '1x 230V',
    },
    settings,
    configuration,
  };
};

const generateSubmissionData = (data: any, changes: any) => {
  // Extract any changes OR fallback to original values
  const { name } = changes.get(STEP_IDS.BASIC) || {};
  const { email } = changes.get(STEP_IDS.EMAIL) || {};
  const address = changes.get(STEP_IDS.ADDRESS) || {};
  const { battery, batteryBrandModel } = changes.get(STEP_IDS.BATTERY) || data.BATTERY || {};
  const converterBrandModel = changes.get(STEP_IDS.CONVERTER) || data.CONVERTER || {};

  const {
    pvArrays = data.PV?.pvArrays,
    pvArraysRetrofit = data.PV?.pvArraysRetrofit,
    retrofitPVMeasurement = data.PV?.retrofitPVMeasurement,
    useUnbiasedEstimators = data.PV?.useUnbiasedEstimators,
  } = changes.get(STEP_IDS.PV) || {};

  const energyTariff = changes.get(STEP_IDS.ENERGY_TARIFF) || data.ENERGY_TARIFF || {};
  const { meterCurrentLimitation } = changes.get(STEP_IDS.METER) || {};
  const { numberOfPhases } = changes.get(STEP_IDS.PHASES) || {};

  // Construct converter's info
  const converterName = converterBrandModel ? `${converterBrandModel.brand} ${converterBrandModel.model}` : undefined;

  const originalConverter = get(data.settings, 'converters[0]', {});
  const converterDetails = getConverterDetailsByBrandModel(converterBrandModel) || originalConverter;
  const converter = omitBy(converterDetails, isUndefined);

  const batteryData = {
    ...originalConverter.battery,
    ...getBatteryDetailsByBrandModel(data.schemas, batteryBrandModel),
    ...battery,
  };

  const converters = [
    {
      ...converter,
      // Battery
      battery: !isEmpty(batteryData) ? batteryData : undefined,
      // PV
      pvArrays: isInvertorHybrid(converterBrandModel) ? pvArrays : null,
    },
  ];

  // Generate cleared settings & configuration
  const settings = omitBy(
    {
      EVChargers: get(data.settings, 'EVChargers'),
      converters,
      pvArraysRetrofit,
      retrofitPVMeasurement,
      useUnbiasedEstimators,
      meterCurrentLimitation,
      numberOfPhases,
    },
    isUndefined,
  );

  if (energyTariff.countryStructure) {
    settings.countryStructure = energyTariff.countryStructure;
  } else {
    settings.tariffStructure = energyTariff.tariffStructure;
    settings.customInjectionTariffStructure = get(data.settings, 'customInjectionTariffStructure');
  }

  const configuration = omitBy(
    {
      address,
      converter: converterName,
    },
    isUndefined,
  );

  return { name, email, settings, configuration };
};

type DeviceConfigWizardComponentProps = {
  schemas?: any;
  isOpen: boolean;
  onClose: () => void;
  thing?: any;
  attributes?: any;
  settings?: any;
  configuration?: any;
  diffObj?: any;
};

const hiddenErrorNames = new Set<string>(['anyOf', 'allOf', 'oneOf']);

function transformErrors(errors: RJSFValidationError[], _: any): RJSFValidationError[] {
  const filterOutHiddenErrorNames = (error: RJSFValidationError) => !error.name || !hiddenErrorNames.has(error.name);

  return errors.filter(filterOutHiddenErrorNames).map((error) => {
    if (error.name === 'required') {
      error.message = 'You must provide a value for this field';
    } else if (error.name === 'maximum') {
      error.message = 'This value is too high, it must be less than or equal to ' + error.params.limit;
    } else if (error.name === 'minimum') {
      error.message = 'This value is too low, it must be greater than or equal to ' + error.message;
    }

    return error;
  });
}

export default function DeviceConfigWizardComponent(props: DeviceConfigWizardComponentProps) {
  const { schemas, isOpen, onClose, thing, attributes, settings, configuration, diffObj } = props;

  const { deviceName: thingName } = useParams<any>();
  const [clientToken] = useState(nanoid());
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [formData, setFormData] = useState<any>(null);
  const [newFormConfig, setNewFormConfig] = useState(formData);
  const [changes, setChanges] = useState(new Map());

  const { setThingName, triggerDeviceData } = useDeviceDataApiHook();

  // Navigation methods
  const totalSteps = () => steps.length;
  const isLastStep = () => activeStep === totalSteps() - 1;
  const handleNext = () => !isLastStep() && setActiveStep(activeStep + 1);
  const handleBack = () => setActiveStep((current) => current - 1);
  const handleStep = (step: any) => () => setActiveStep(step);

  const readyToSave = isReadyToSave(data, changes);

  const onSubmit = async () => {
    if (!readyToSave) {
      throw new Error('Not ready to save, please check validation');
    }

    setLoading(true);

    try {
      const { name, email, settings, configuration } = generateSubmissionData(data, changes);

      // Updated `/config`
      const shouldSaveConfig = !isEmpty(settings) || !isEmpty(configuration);
      if (shouldSaveConfig) {
        const body = {
          formData: {
            settings,
            configuration,
          },
          clientToken,
        };
        await API.post('IO_API', `/devices/${thingName}/config`, { body });
      }

      // Update `name`
      const shouldSaveName = !!name;
      if (shouldSaveName) {
        await API.post('IO_API', `/devices/${thingName}/name`, {
          body: { name: nameUtils.encode(name) },
        });
      }

      // Update `email`
      const shouldSaveEmail = !!email;
      if (shouldSaveEmail) {
        await API.post('IO_API', `/devices/${thingName}/link`, {
          body: { email: email.toLowerCase() },
        });
      }

      setLoading(false);

      await triggerDeviceData();

      onClose();
    } catch (e: any) {
      const errorMessage = e?.response?.data || `${e}`;
      console.error('[config wizard: submit]', errorMessage);
      setError(errorMessage);
      setSaveDialogOpen(false);
      handleStep(steps.length - 1);
      setLoading(false);
    }
  };

  const onChange = (stepId: any, formRef: any, additionalData?: any) => (key: any) => {
    let formData = get(formRef, 'current.state.formData', null);
    if (!formData) {
      return;
    }

    const newChanges = deepClone(changes);
    if (key && typeof key === 'string') {
      const content = changes.get(stepId);
      formData = {
        ...content,
        [key]: formData[key],
      };
    }

    if (additionalData) {
      for (const [key, value] of Object.entries(additionalData)) {
        formData[key] = value;
      }
    }

    newChanges.set(
      stepId,
      omitBy(formData, (c) => c === undefined),
    );

    setChanges(newChanges);
  };

  const onClear = (stepId: any) => (propertyToClear: any) => {
    setChanges((collection) => {
      const content = collection.get(stepId);
      collection.set(stepId, omit(content, [propertyToClear]));
      return collection;
    });
  };

  const close = () => {
    setSaveDialogOpen(true);
  };

  const initialize = () => {
    setError(false);
    setData(null);
  };

  useEffect(() => {
    isOpen && initialize();
  }, [isOpen]);

  useEffect(() => {
    if (!schemas) {
      return;
    }

    setData({ schemas, ...generateDeviceData(attributes, configuration, settings, schemas) });
  }, [schemas, attributes, configuration, settings]);

  useEffect(() => {
    const newFormData = updateConvertersData(newFormConfig, formData);
    setFormData(newFormData);
  }, [newFormConfig]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const { configuration = {}, settings = {} } = data as any;
    const config = generateNewFormConfig(configuration, settings, {});
    setNewFormConfig(config);
  }, [data]);

  useEffect(() => {
    setThingName(thingName);
  }, [thingName]);

  const activeStepComponentProps = useMemo(() => {
    if (!data) {
      return {};
    }

    const { id: stepId, dataFilteringFunction, ...stepProps } = steps[activeStep] as any;

    const schema: any = generateStepSchema(stepProps, data, changes.get(stepId));
    (stepProps as any).uiSchema = getUiSchemaBasedOnSchemaAttributes(schema, stepProps);

    const filteredStepData = dataFilteringFunction ? dataFilteringFunction((data as any)[stepId], schema) : (data as any)[stepId];

    const props = {
      ...(data as any),
      data,
      stepId,
      onChange,
      onClear,
      onSubmit,
      loading,
      state: changes,
      error,
      steps,
      stepProps: {
        ...stepProps,
        validator,
        schema,
        liveValidate: !!changes.get(stepId),
        showErrorList: false,
        liveOmit: true,
        omitExtraData: true,
        formData: changes.get(stepId) || filteredStepData,
        formContext: {
          diffObj: (steps[activeStep] as any).diffFunction ? (steps[activeStep] as any).diffFunction(diffObj, thing, configuration, settings) : diffObj,
        },
        transformErrors,
        templates: {
          FieldTemplate: CustomFieldTemplate,
        },
      },
    };

    if (stepProps.customValidator) {
      props.stepProps.customValidate = (formData: any, errors: RJSFValidationError[], uiSchema?: any) => {
        return stepProps.customValidator(formData, errors, uiSchema, data, changes);
      };
    }

    return props;
  }, [data, changes, activeStep, loading, error]);

  const ActiveStepComponent = steps[activeStep].component;

  if (!data) {
    return <LinearProgress />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <SaveDialog
        isOpen={saveDialogOpen}
        readyToSave={readyToSave}
        onSave={onSubmit}
        onClose={onClose}
        onCancel={() => setSaveDialogOpen(false)}
        loading={loading}
      />
      {/* The stepper containing the titles */}
      <Stepper
        sx={stepperStyles}
        nonLinear
        activeStep={activeStep}
        alternativeLabel
      >
        {steps.map(({ id, title }, index) => (
          <Step key={title}>
            <StepLabel
              onClick={handleStep(index)}
              error={!validateStep(id, data, changes)}
            >
              {title}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box>
        {/* Active step component */}
        <ActiveStepComponent {...activeStepComponentProps} />

        {/* Actions */}
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          {activeStep !== steps.length - 1 ? (
            <Button
              onClick={handleNext}
              sx={{ mr: 1 }}
            >
              Next
            </Button>
          ) : (
            ''
          )}
          <Button onClick={close}>Close</Button>
        </Box>
      </Box>
    </Box>
  );
}
